/**
 * Deliberate crash trigger, for verifying that crash reporting actually works on
 * a real deployment.
 *
 * The chain worth testing is: a throw during render, caught by app/error.tsx,
 * which reports via lib/crash-report.ts to /api/bug-report and shows the returned
 * BR- id. Only a synchronous throw during render enters it — Next 15 routes
 * neither unhandled rejections nor window.onerror to the error boundary in
 * production — and there is no way to inject into React's render phase from
 * outside the bundle. Hence a trigger that lives in the code.
 *
 * The message is deliberately stable rather than carrying a nonce, so a selftest
 * crash takes exactly the same path as a real one, including the dedupe in
 * sendCrashReport. Consequence worth knowing while testing: pressing
 * "Spróbuj ponownie" with the parameter still in the URL throws the identical
 * error, which the dedupe suppresses, so the screen offers a retry button rather
 * than a second id. Reload to get another report.
 */

/** Marks the crash as self-inflicted, so these are filterable in the report store. */
export const CRASH_SELFTEST_MESSAGE = 'Selftest: deliberate render crash, verifying crash reporting';

export const CRASH_SELFTEST_PARAM = 'selftest';
export const CRASH_SELFTEST_VALUE = 'crash';

/**
 * The second mode, and the reason it exists.
 *
 * `?selftest=crash` is typed into the address bar, which is a full page load. That
 * resets the job context (module state) and empties the in-memory log, so its
 * report always arrives with `=== Job ===` holding nothing but a heap figure and
 * no document name anywhere — which makes it useless for the one thing most worth
 * checking, that names are redacted from automatic uploads. Three real reports
 * demonstrated this before anyone noticed the procedure could not work.
 *
 * So this mode does not throw on load. The parameter rides along in the URL while
 * the user processes a file normally, and the crash fires on the render where the
 * finished text would appear — job context populated, log full of the file's name.
 */
export const CRASH_AFTER_JOB_VALUE = 'crash-after-ocr';

export const CRASH_AFTER_JOB_MESSAGE =
    'Selftest: deliberate render crash after a completed job, verifying redaction';

/**
 * Whether the current URL is asking for a crash.
 *
 * `search` is injectable for tests; left out, it reads the live location and
 * returns false during server rendering. That guard is load-bearing: this is a
 * client component, but Next still renders it on the server, and throwing there
 * would produce a server error instead of the client boundary under test.
 */
export function isCrashSelftestRequested(search?: string): boolean {
    const query = search ?? (typeof window === 'undefined' ? '' : window.location.search);
    if (!query) {
        return false;
    }

    try {
        return new URLSearchParams(query).get(CRASH_SELFTEST_PARAM) === CRASH_SELFTEST_VALUE;
    } catch {
        // A malformed query string must never take the app down.
        return false;
    }
}

/** Throws when, and only when, the URL asks for it. Call during render. */
export function throwIfCrashSelftestRequested(search?: string): void {
    if (isCrashSelftestRequested(search)) {
        throw new Error(CRASH_SELFTEST_MESSAGE);
    }
}

export function isCrashAfterJobRequested(search?: string): boolean {
    const query = search ?? (typeof window === 'undefined' ? '' : window.location.search);
    if (!query) {
        return false;
    }

    try {
        return new URLSearchParams(query).get(CRASH_SELFTEST_PARAM) === CRASH_AFTER_JOB_VALUE;
    } catch {
        return false;
    }
}

/**
 * Throws once a job has reached the display stage, and only when asked.
 *
 * The stage is passed in rather than read here, so this stays free of any
 * dependency on job context and can be tested as a pure function.
 */
export function throwIfCrashAfterJobRequested(jobStage: string | undefined, search?: string): void {
    if (jobStage === 'display' && isCrashAfterJobRequested(search)) {
        throw new Error(CRASH_AFTER_JOB_MESSAGE);
    }
}
